import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Badge, Row, Col, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import Pager from './Pager';
import { formatDay } from '../utils/dates';
import Loading from './Loading';

const PAGE_SIZE = 15;
const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const INVOICE_STATUS = { draft: 'borrador', ready: 'lista, sin enviar', pending_dgii: 'esperando a DGII' };

// Conduces → "Sin facturar" (backend services/unbilledConduces.js): delivered sale conduces no factura bills — a rental's
// conduces never show, its rental bills it. "Facturar" opens Facturas with the conduce's lines (the factura then bills
// it). A factura that probably belongs to the conduce — same client, made around then, billing no conduce — is shown,
// and an admin can link it ("Es esta"). `data` / `error` / `onChanged` come from ConducesPage, which loads the list once
// for the tab's badge too. Ported from Caribbean Gas' Ventas, where a conduce is billed through its pedido.
const UnbilledConducesPanel = ({ data, error, onChanged, isAdmin }) => {
  const navigate = useNavigate();
  const rows = data?.rows ?? null;
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => { setPage(1); }, [search, client]);

  const byClient = useMemo(() => {
    const map = new Map();
    for (const r of rows || []) {
      const key = String(r.client?._id || '');
      const entry = map.get(key) || { id: key, name: r.client?.name || '—', conduces: 0, units: 0 };
      entry.conduces += 1; entry.units += r.units;
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => b.conduces - a.conduces || b.units - a.units);
  }, [rows]);

  const term = norm(search.trim());
  const filtered = useMemo(() => (rows || []).filter(r => (!client || String(r.client?._id) === client)
    && (!term || [r.number, r.client?.name, r.client?.rnc, r.reference].some(v => norm(v).includes(term)))), [rows, client, term]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const totalValue = (rows || []).reduce((s, r) => s + (r.value || 0), 0);
  const withPossible = (rows || []).filter(r => r.possible.length > 0).length;

  const link = async (r, f) => {
    if (!window.confirm(`¿El conduce ${r.number} se facturó en la factura ${f.number}? Quedará vinculado a ella y sale de esta lista.`)) return;
    setBusy(r._id); setActionError(''); setNotice('');
    try {
      const { data: res } = await API.post(`/conduces/${r._id}/invoice`, { invoiceId: f._id });
      setNotice(res.message || 'Vinculado.');
      onChanged?.();
    } catch (err) { setActionError(err.response?.data?.message || 'No se pudo vincular.'); }
    finally { setBusy(''); }
  };

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (rows === null) return <Loading label="Cargando conduces sin facturar..." />;

  return (
    <>
      <div className="text-muted small mb-3">
        Conduces de venta entregados que ninguna factura incluye: equipos o productos que salieron y no se han facturado. Los
        conduces de un alquiler no aparecen (se facturan con su alquiler), ni los que paga un acuerdo con el cliente (pestaña
        Acuerdos). Si uno se facturó en una factura hecha sin partir del conduce, se muestra la que probablemente le corresponde.
        {data?.since && <> Solo conduces desde el {formatDay(data.since)}.</>}
      </div>
      {actionError && <div className="alert alert-danger">{actionError}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      <Row className="mb-3">
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Conduces sin facturar</div>
            <h2 className="mt-2 mb-0" style={{ color: rows.length ? 'var(--mm-yellow)' : 'var(--mm-green)' }}>{rows.length}</h2>
          </Card>
        </Col>
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Su valor (sin ITBIS)</div>
            <h2 className="mt-2 mb-0">{money(totalValue)}</h2>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Con una factura que probablemente les corresponde</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{withPossible}</h2>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm p-3">
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Form.Control style={{ maxWidth: 320 }} placeholder="Buscar por número, cliente o referencia..." value={search} onChange={e => setSearch(e.target.value)} />
          <Form.Select style={{ maxWidth: 320 }} value={client} onChange={e => setClient(e.target.value)}>
            <option value="">Todos los clientes ({byClient.length})</option>
            {byClient.map(c => <option key={c.id} value={c.id}>{c.name} — {c.conduces} conduce{c.conduces === 1 ? '' : 's'}</option>)}
          </Form.Select>
        </div>
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle">
            <thead>
              <tr><th>Conduce</th><th>Cliente</th><th>Entregado</th><th>Contenido</th><th>¿Ya facturado?</th><th /></tr>
            </thead>
            <tbody>
              {slice.map(r => (
                <tr key={r._id}>
                  <td>
                    <strong>{r.number}</strong>
                    {r.reference && <div className="small text-muted">Ref. {r.reference}</div>}
                    {r.value != null && <div className="small text-muted">{money(r.value)}</div>}
                    {r.deal && <div className="small" style={{ color: 'var(--mm-cyan)' }}>Acuerdo cubre {money(r.deal.covered)} de {money(r.deal.value)}</div>}
                  </td>
                  <td>{r.client?.name || '—'}</td>
                  <td className="small">{formatDay(r.deliveredAt || r.createdAt)}</td>
                  <td className="small">{r.items.map((it, i) => <div key={i}>{it.quantity} × {it.description}</div>)}</td>
                  <td className="small">
                    {r.onTheWay && (
                      <div><Badge bg="info">En proceso</Badge> {r.onTheWay.number} ({INVOICE_STATUS[r.onTheWay.status] || r.onTheWay.status})</div>
                    )}
                    {!r.onTheWay && r.possible.map(f => (
                      <div key={f._id} className="d-flex align-items-center gap-1 flex-wrap">
                        <span style={{ color: 'var(--mm-cyan)' }}>Posible:</span>
                        <a href={`/invoices/${f._id}/print`} target="_blank" rel="noreferrer">{f.number}</a>
                        <span>{formatDay(f.createdAt)} · {money(f.total)}</span>
                        {!f.sameProducts && <span className="text-muted">(otros productos)</span>}
                        {isAdmin && <Button size="sm" variant="link" className="p-0 ms-1" disabled={busy === r._id} onClick={() => link(r, f)}>Es esta</Button>}
                      </div>
                    ))}
                    {!r.onTheWay && r.possible.length === 0 && <span className="text-muted">No</span>}
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      {!r.onTheWay && (
                        <Button size="sm" variant="outline-primary" onClick={() => navigate(`/invoices/new?conduce=${r._id}`)}><i className="bi bi-receipt" /> Facturar</Button>
                      )}
                      <Button size="sm" variant="outline-secondary" onClick={() => window.open(`/conduces/${r._id}/print`, '_blank')}><i className="bi bi-printer" /> Ver</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {slice.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted py-4">{rows.length ? 'Ningún conduce coincide.' : 'Todos los conduces entregados están facturados.'}</td></tr>
              )}
            </tbody>
          </Table>
        </div>
        {pages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">Página {current} de {pages} — {filtered.length} conduce{filtered.length === 1 ? '' : 's'}</span>
            <Pager page={current} pages={pages} onChange={setPage} />
          </div>
        )}
      </Card>
    </>
  );
};

export default UnbilledConducesPanel;
